import { axiosClient } from './axiosClient';

// Types
export interface AdminGlobalStats {
  // Listing stats
  activeListings: number;        // marketplace_active + exchange_active
  marketplaceActive: number;     // Only marketplace listings
  exchangeActive: number;        // Only exchange listings
  soldListings: number;
  exchangedListings: number;
  totalListings: number;

  // Transaction stats
  totalSales: number;
  totalExchanges: number;
  totalTransactions: number;     // totalSales + totalExchanges

  // User engagement stats
  totalOffers: number;
  totalReviews: number;
  totalFavorites: number;
}

export interface AdminCategoryStats {
  _id: string;
  name: string;
  activeListings: number;
  totalSales: number;
  totalExchanges: number;
}

export interface AdminTopListing {
  _id: string;
  title: string;
  seller: string;
  favorites: number;
  offers: number;
}

export interface AdminCounterHealth {
  summary: {
    totalCounters: number;
    totalCountValue: number;
    byName: Record<string, { counters: number; totalValue: number }>;
  };
  details: Array<{
    _id: { name: string; type: string };
    count: number;
    totalValue: number;
    avgValue: number;
    maxValue: number;
    minValue: number;
  }>;
}

export interface AdminOfferStats {
  totalOffers: number;
  pendingOffers: number;
  acceptedOffers: number;
  declinedOffers: number;
  expiredOffers: number;
  withdrawnOffers: number;
  counterOfferedOffers: number;
  cashOffers: number;
  exchangeOffers: number;
}

export interface AdminReviewStats {
  totalReviews: number;
  averageRating: number;
  ratingDistribution: { 1: number; 2: number; 3: number; 4: number; 5: number };
  buyerReviews: number;
  sellerReviews: number;
}

// API Functions
export const getAdminGlobalStats = async (): Promise<AdminGlobalStats> => {
  const { data } = await axiosClient.get('/marketplace/admin/stats/global');
  return data.data;
};

export const getAdminCategoryStats = async (
  limit: number = 20,
  sortBy: 'active_listings' | 'total_sales' | 'total_exchanges' = 'active_listings'
): Promise<AdminCategoryStats[]> => {
  const { data } = await axiosClient.get('/marketplace/admin/stats/categories', {
    params: { limit, sortBy },
  });
  return data.data;
};

export const getAdminTopListings = async (
  metric: 'favorites' | 'offers' = 'offers',
  limit: number = 10
): Promise<AdminTopListing[]> => {
  const { data } = await axiosClient.get('/marketplace/admin/stats/top-listings', {
    params: { metric, limit },
  });
  return data.data;
};

export const getAdminCounterHealth = async (): Promise<AdminCounterHealth> => {
  const { data } = await axiosClient.get('/marketplace/admin/stats/counter-health');
  return data.data;
};

export const getAdminOfferStats = async (): Promise<AdminOfferStats> => {
  const { data } = await axiosClient.get('/marketplace/admin/stats/offers');
  return data.data;
};

export const getAdminReviewStats = async (): Promise<AdminReviewStats> => {
  const { data } = await axiosClient.get('/marketplace/admin/stats/reviews');
  return data.data;
};
